import { workerManager as defaultWorkerManager } from '../../worker'
import { createVisibleModelExportRoot } from './createVisibleModelExportRoot'
import { createPuppyTopologyRuntimeDocument } from './createPuppyTopologyRuntimeDocument'
import { encodePuppyTopologyRuntimeDocument } from './topologyRuntimeBinaryCodec'
import { exportVisibleModelRootToGlbBytes } from './exportGlb'
import { createPuppyProjectModelExportResult } from './createPuppyProjectModelDocument'
//------ 线性零部件阵列离线包定义持久化 开始------
import {
  createPuppyLinearComponentPatternExtension,
  writePuppyLinearComponentPatternExtensionToAssemblyExtensions,
} from './puppyLinearComponentPatternExtension'
//------ 线性零部件阵列离线包定义持久化 结束------
import {
  validatePuppyAssemblyMateGraph,
  validatePuppyProjectModelGraph,
} from './validatePuppyPackageGraph'
//------ 草图离线包定义持久化 开始------
import {
  createPuppySketchExtension,
  writePuppySketchExtensionToAssemblyExtensions,
} from './puppySketchExtension'
import {
  createPuppySketchEntityReferenceExtension,
  writePuppySketchEntityReferenceExtensionToAssemblyExtensions,
} from './puppySketchEntityReferenceExtension'
import {
  createPuppyFeatureExtension,
  writePuppyFeatureExtensionToAssemblyExtensions,
} from './puppyFeatureExtension'
import {
  createPuppySectionViewExtension,
  writePuppySectionViewExtensionToAssemblyExtensions,
} from './puppySectionViewExtension'
//------ 草图离线包定义持久化 结束------
import {
  createSha256Hex,
  encodeUtf8,
  normalizePuppyExportFileBaseName,
} from './puppyPackageUtils'
import {
  createPuppyContentPackageId,
  createPuppyPackageContentDigest,
  stringifyCanonicalPuppyJson,
} from './puppyPackageCanonicalContent'
import type {
  ExportPuppyPackageOptions,
  PuppyBrepIndexDocument,
  PuppyBrepIndexEntry,
  PuppyPackageDocumentType,
  PuppyPackageManifest,
  PuppyPackageExportResult,
  PuppyPackageExportWorkerClient,
  PuppyVisibleModelExportRoot,
} from './types'
import {
  PUPPY_ASSEMBLY_PATH,
  PUPPY_BREP_DIRECTORY,
  PUPPY_BREP_INDEX_PATH,
  PUPPY_DISPLAY_GLB_PATH,
  PUPPY_MANIFEST_PATH,
  PUPPY_CONTAINER_VERSION,
  PUPPY_PACKAGE_FORMAT,
  PUPPY_PACKAGE_VERSION,
  PUPPY_PROJECT_MODEL_PATH,
  PUPPY_TOPOLOGY_RUNTIME_ENCODING,
  PUPPY_TOPOLOGY_RUNTIME_PATH,
} from './types'

/**
 * 读取导出根中绑定到 Worker 的几何对象 ID。
 *
 * @param exportRoot 可见模型导出根。
 * @returns 去重后的几何对象 ID。
 */
function collectGeometryObjectIds(exportRoot: PuppyVisibleModelExportRoot): string[] {
  return Array.from(new Set(
    exportRoot.nodeBindings.flatMap((binding) => binding.geometryObjectIds),
  )).sort()
}

/**
 * 校验可见模型是否可生成完整离线包。
 *
 * @param exportRoot 可见模型导出根。
 */
function assertVisibleModelCanExportPuppyPackage(exportRoot: PuppyVisibleModelExportRoot): void {
  if (exportRoot.diagnostics.exportedMeshCount <= 0) {
    throw new Error('当前场景没有可导出的可见模型')
  }

  if (exportRoot.diagnostics.ambiguousBrepBindingMeshCount > 0) {
    throw new Error(`当前可见模型中有 ${exportRoot.diagnostics.ambiguousBrepBindingMeshCount} 个网格缺少 body/primitive 级精确映射，无法导出可完整恢复拓扑交互的离线包`)
  }

  if (exportRoot.diagnostics.missingBrepBindingMeshCount > 0) {
    throw new Error(`当前可见模型中有 ${exportRoot.diagnostics.missingBrepBindingMeshCount} 个网格缺少 BRep 绑定，无法导出可完整恢复拓扑的离线包`)
  }

  if (exportRoot.diagnostics.missingTopologySnapshotMeshCount > 0) {
    throw new Error(`当前可见模型中有 ${exportRoot.diagnostics.missingTopologySnapshotMeshCount} 个网格缺少可导入消费的 topologySnapshot，无法导出高性能离线包`)
  }

  if (collectGeometryObjectIds(exportRoot).length <= 0) {
    throw new Error('当前可见模型没有绑定到 Worker BRep Shape，无法导出离线包')
  }
}

/**
 * 生成 BRep 文件内部路径。
 *
 * @param sha256 BRep 文件 SHA-256。
 * @returns ZIP 内部路径。
 */
function buildBrepPath(sha256: string): string {
  return `${PUPPY_BREP_DIRECTORY}/${sha256}.brep`
}

/**
 * 将导出的 BRep 记录转换为 zip 文件条目和索引条目。
 *
 * @param records Worker 返回的 BRep 记录。
 * @returns BRep 文件与索引条目。
 */
async function buildBrepPackageEntries(records: ExportBrepShapeRecord[]): Promise<{
  brepFiles: Record<string, Uint8Array>
  indexEntries: PuppyBrepIndexEntry[]
}> {
  const brepFiles: Record<string, Uint8Array> = {}
  const indexEntries: PuppyBrepIndexEntry[] = []

  const sortedRecords = [...records].sort((left, right) => left.objectId.localeCompare(right.objectId))
  for (const record of sortedRecords) {
    const payloadBytes = encodeUtf8(record.payload)
    const sha256 = await createSha256Hex(payloadBytes)
    const brepPath = buildBrepPath(sha256)

    /**
     * 相同 BRep payload 只写一份文件，多个几何对象通过 brepSha256 指向同一条定义。
     * 这比复制原项目文件镜像更轻，也保留了后续导入恢复真实拓扑所需的权威 Shape。
     */
    brepFiles[brepPath] = payloadBytes
    indexEntries.push({
      geometryObjectId: record.objectId,
      // Worker 导出的是当前注册 Shape，重载不得因 SHA 去重再次按共享源拆分。
      shapeScope: 'geometry-object',
      name: record.name,
      encoding: record.encoding,
      brepSha256: sha256,
      brepPath,
      byteLength: payloadBytes.byteLength,
      commandId: record.commandId,
    })
  }

  return {
    brepFiles,
    indexEntries,
  }
}

/**
 * 校验 Worker BRep 导出结果是否覆盖所有可见模型绑定。
 *
 * @param input 校验输入。
 */
function assertBrepExportComplete(input: {
  requestedObjectIds: string[]
  brepResult: ExportBrepShapesResult
}): void {
  if (input.brepResult.records.length <= 0) {
    const skippedText = input.brepResult.skipped
      .map((record) => `${record.objectId}: ${record.reason}`)
      .join('；')
    throw new Error(`Worker 未导出任何 BRep Shape${skippedText ? `：${skippedText}` : ''}`)
  }

  const exportedObjectIds = new Set(input.brepResult.records.map((record) => record.objectId))
  const missingObjectIds = input.requestedObjectIds.filter((objectId) => !exportedObjectIds.has(objectId))
  if (missingObjectIds.length > 0) {
    const skippedText = input.brepResult.skipped
      .filter((record) => missingObjectIds.includes(record.objectId))
      .map((record) => `${record.objectId}: ${record.reason}`)
      .join('；')
    throw new Error(`以下可见模型缺少可导出的 BRep Shape: ${missingObjectIds.join(', ')}${skippedText ? `；${skippedText}` : ''}`)
  }
}

/**
 * 创建离线包 manifest。
 *
 * @param input 创建输入。
 * @returns manifest。
 */
function createPuppyManifest(input: {
  projectName: string
  documentType: PuppyPackageDocumentType
  exportRoot: PuppyVisibleModelExportRoot
  brepFileCount: number
  geometryObjectCount: number
  hasProjectModel: boolean
  contentDigest: string
}): PuppyPackageManifest {
  return {
    format: PUPPY_PACKAGE_FORMAT,
    version: PUPPY_CONTAINER_VERSION,
    packageId: createPuppyContentPackageId(input.contentDigest),
    projectName: input.projectName,
    documentType: input.documentType,
    contentDigest: input.contentDigest,
    displayFile: PUPPY_DISPLAY_GLB_PATH,
    brepIndexFile: PUPPY_BREP_INDEX_PATH,
    assemblyFile: PUPPY_ASSEMBLY_PATH,
    ...(input.hasProjectModel ? { projectModelFile: PUPPY_PROJECT_MODEL_PATH } : {}),
    topologyRuntimeFile: PUPPY_TOPOLOGY_RUNTIME_PATH,
    topologyRuntimeEncoding: PUPPY_TOPOLOGY_RUNTIME_ENCODING,
    visibleMeshCount: input.exportRoot.diagnostics.exportedMeshCount,
    brepFileCount: input.brepFileCount,
    geometryObjectCount: input.geometryObjectCount,
    diagnostics: input.exportRoot.diagnostics,
  }
}

/**
 * 从 display body 绑定中建立 BRep 条目的显示名索引。
 *
 * @param exportRoot 可见模型导出根。
 * @returns 几何对象 ID 到用户可见名称的映射。
 */
function createBrepEntryDisplayNameMap(exportRoot: PuppyVisibleModelExportRoot): Map<string, string> {
  const nameByGeometryObjectId = new Map<string, string>()
  exportRoot.nodeBindings.forEach((nodeBinding) => {
    nodeBinding.bodyBindings.forEach((bodyBinding) => {
      const geometryObjectId = bodyBinding.geometryObjectId.trim()
      const displayName = bodyBinding.name?.trim() ?? ''
      if (geometryObjectId && displayName && !nameByGeometryObjectId.has(geometryObjectId)) {
        nameByGeometryObjectId.set(geometryObjectId, displayName)
      }
    })
  })
  return nameByGeometryObjectId
}

/**
 * 创建离线包 BRep 索引。
 *
 * @param input 创建输入。
 * @returns BRep 索引文档。
 */
function createBrepIndexDocument(input: {
  exportRoot: PuppyVisibleModelExportRoot
  entries: PuppyBrepIndexEntry[]
}): PuppyBrepIndexDocument {
  const displayNameByGeometryObjectId = createBrepEntryDisplayNameMap(input.exportRoot)
  return {
    version: PUPPY_PACKAGE_VERSION,
    displayNodes: [...input.exportRoot.nodeBindings]
      .sort((left, right) => left.displayNodeId.localeCompare(right.displayNodeId))
      .map((binding) => ({
        ...binding,
        geometryObjectIds: [...binding.geometryObjectIds].sort(),
        bodyBindings: [...binding.bodyBindings]
          .sort((left, right) => left.bodyBindingId.localeCompare(right.bodyBindingId)),
      })),
    entries: [...input.entries]
      .sort((left, right) => left.geometryObjectId.localeCompare(right.geometryObjectId))
      .map((entry) => {
      const displayName = displayNameByGeometryObjectId.get(entry.geometryObjectId)
      return displayName
        ? {
            ...entry,
            name: displayName,
          }
        : entry
      }),
  }
}

/**
 * 导出 PuppyCAD 离线包。
 *
 * @param options 导出选项。
 * @returns `.puppy` 文件结果。
 */
export async function exportPuppyPackage(options: ExportPuppyPackageOptions): Promise<PuppyPackageExportResult> {
  const fileBaseName = normalizePuppyExportFileBaseName(options.fileBaseName)
  const documentType = options.documentType === 'assembly' ? 'assembly' : 'part'
  const exportRoot = createVisibleModelExportRoot(options.sceneRoot, {
    mates: options.mates,
    datumExtension: options.datumExtension,
    assemblySemanticSource: options.assemblySemanticSource,
    resultBodyDisplayNameByObjectId: options.resultBodyDisplayNameByObjectId,
    documentType,
  })
  //------ 线性零部件阵列离线包定义持久化 开始------
  // 可见模型导出完成后才具备“运行时对象 ID → 最终装配节点 ID”的权威映射。
  const linearComponentPatternExtension = createPuppyLinearComponentPatternExtension({
    document: options.linearComponentPatternDocument,
    sceneRoot: options.sceneRoot,
    runtimeSourceObjectIdsByAssemblyNodeId: exportRoot.runtimeSourceObjectIdsByAssemblyNodeId ?? {},
    // imported BRep 拓扑引用必须以最终 display/body 绑定归一为 occurrence 级稳定身份。
    nodeBindings: exportRoot.nodeBindings,
  })
  exportRoot.assemblyDocument.extensions =
    writePuppyLinearComponentPatternExtensionToAssemblyExtensions(
      exportRoot.assemblyDocument.extensions,
      linearComponentPatternExtension,
    )
  //------ 线性零部件阵列离线包定义持久化 结束------
  //------ 草图离线包定义持久化 开始------
  // 草图扩展在写入前逐份重新校验并按 sketchId 字典序排序，保证连续保存内容一致。
  const sketchExtension = createPuppySketchExtension(options.sketchExtensionEntries)
  exportRoot.assemblyDocument.extensions = writePuppySketchExtensionToAssemblyExtensions(
    exportRoot.assemblyDocument.extensions,
    sketchExtension,
  )
  // Record 是转换实体引用的唯一持久状态；求解层 OnEdge 不随草图关系重复写入。
  const sketchEntityReferenceExtension = createPuppySketchEntityReferenceExtension(
    options.sketchEntityReferenceRecords,
  )
  exportRoot.assemblyDocument.extensions = writePuppySketchEntityReferenceExtensionToAssemblyExtensions(
    exportRoot.assemblyDocument.extensions,
    sketchEntityReferenceExtension,
  )
  //------ 草图离线包定义持久化 结束------
  //------ 特征离线包定义持久化 开始------
  const featureExtension = createPuppyFeatureExtension(options.featureExtensionInput)
  exportRoot.assemblyDocument.extensions = writePuppyFeatureExtensionToAssemblyExtensions(
    exportRoot.assemblyDocument.extensions,
    featureExtension,
  )
  //------ 特征离线包定义持久化 结束------
  // 剖面视图是文档级视图状态，不进入 GLB/B-Rep；只写入版本化 assembly 扩展。
  const sectionViewExtension = createPuppySectionViewExtension(options.sectionViewSnapshot)
  exportRoot.assemblyDocument.extensions = writePuppySectionViewExtensionToAssemblyExtensions(
    exportRoot.assemblyDocument.extensions,
    sectionViewExtension,
  )
  assertVisibleModelCanExportPuppyPackage(exportRoot)
  // assembly 语义已经完成重绑；此处先阻断悬空 Mate，避免执行昂贵的 GLB 与 Worker 导出后才暴露坏包。
  validatePuppyAssemblyMateGraph(exportRoot.assemblyDocument)

  const displayGlbBytes = await exportVisibleModelRootToGlbBytes(exportRoot)
  const requestedObjectIds = collectGeometryObjectIds(exportRoot)
  const workerClient: PuppyPackageExportWorkerClient = options.workerManager ?? defaultWorkerManager
  /**
   * `includeConsumedBaselines`：融合/切除会吸收导入基线体，它们是特征时间线的重放输入。
   * 不落包则重载重放时融合找不到对象，命令退化为独立结果体，导入几何在基线释放后永久丢失。
   */
  const brepResult = await workerClient.exportBrepShapes({
    objectIds: requestedObjectIds,
    includeConsumedBaselines: true,
  })
  assertBrepExportComplete({ requestedObjectIds, brepResult })

  const brepPackageEntries = await buildBrepPackageEntries(brepResult.records)
  const brepIndex = createBrepIndexDocument({
    exportRoot,
    entries: brepPackageEntries.indexEntries,
  })
  const topologyRuntime = createPuppyTopologyRuntimeDocument(exportRoot)
  topologyRuntime.assets.sort((left, right) => left.bodyBindingId.localeCompare(right.bodyBindingId))
  const projectModelExport = documentType === 'assembly'
      ? createPuppyProjectModelExportResult({
        assembly: exportRoot.assemblyDocument,
        projectModelSource: options.projectModelSource,
      })
    : null
  const projectModel = projectModelExport?.document ?? null
  if (projectModel) {
    validatePuppyProjectModelGraph({
      assembly: exportRoot.assemblyDocument,
      projectModel,
    })
  }
  const businessFiles: Record<string, Uint8Array> = {
    [PUPPY_DISPLAY_GLB_PATH]: displayGlbBytes,
    [PUPPY_BREP_INDEX_PATH]: encodeUtf8(stringifyCanonicalPuppyJson(brepIndex)),
    [PUPPY_ASSEMBLY_PATH]: encodeUtf8(stringifyCanonicalPuppyJson(exportRoot.assemblyDocument)),
    ...(projectModel
      ? { [PUPPY_PROJECT_MODEL_PATH]: encodeUtf8(stringifyCanonicalPuppyJson(projectModel)) }
      : {}),
    [PUPPY_TOPOLOGY_RUNTIME_PATH]: encodePuppyTopologyRuntimeDocument(topologyRuntime),
    ...Object.fromEntries(Object.entries(brepPackageEntries.brepFiles).sort(([left], [right]) => (
      left.localeCompare(right)
    ))),
  }
  const contentDigest = await createPuppyPackageContentDigest(businessFiles)
  const manifest = createPuppyManifest({
    projectName: fileBaseName,
    documentType,
    exportRoot,
    brepFileCount: Object.keys(brepPackageEntries.brepFiles).length,
    geometryObjectCount: requestedObjectIds.length,
    hasProjectModel: Boolean(projectModel),
    contentDigest,
  })
  const { zipSync } = await import('three/examples/jsm/libs/fflate.module.js')

  const bytes = zipSync({
    [PUPPY_MANIFEST_PATH]: encodeUtf8(stringifyCanonicalPuppyJson(manifest)),
    ...businessFiles,
  }, { level: 6 })

  return {
    fileName: `${fileBaseName}.puppy`,
    mimeType: 'application/vnd.puppycad.offline+zip',
    bytes,
    rootDirectOccurrenceIdentities: projectModelExport?.rootDirectOccurrenceIdentities ?? [],
  }
}
